<template>
  <main class="manual" :class="{ 'manual--night': isDark }">
    <a class="skip-link" href="#manual-content">Ir para o conteúdo</a>

    <section v-if="!hasDocumentation" class="manual-empty">
      <h1>Esta central ainda está sendo preparada.</h1>
      <p>Quando os materiais deste portal estiverem disponíveis, eles aparecerão aqui para consulta da sua equipe.</p>
      <button type="button" class="text-button" @click="$router.go(-1)">Voltar</button>
    </section>

    <template v-else>
      <header class="manual-hero">
        <div class="manual-shell">
          <p class="manual-context">{{ tenantConfig.companyName }} · Central de ajuda</p>
          <h1>{{ tenantConfig.title || documentation.title }}</h1>
          <p>{{ tenantConfig.description || documentation.intro }}</p>
          <p class="updated">Atualizado em {{ documentation.updatedAt }}</p>
          <label class="search-box">
            <input v-model="searchTerm" type="search" autocomplete="off" placeholder="Procure por pedido, boleto, cadastro..." aria-label="Buscar em toda a documentação">
            <button v-if="searchTerm" type="button" class="search-clear" @click="clearSearch">Limpar</button>
          </label>
          <p class="search-hint">Pesquise em títulos, orientações e passos. A busca ignora acentos. Pressione / para buscar.</p>
        </div>
      </header>

      <div id="manual-content" class="manual-shell manual-layout">
        <aside class="manual-aside" aria-label="Navegação da documentação">
          <div class="aside-sticky">
            <h2 class="aside-label">Sumário</h2>
            <nav class="toc">
              <a v-for="section in visibleSections" :key="section.id" :href="'#' + section.id" :class="{ active: activeSection === section.id }" @click.prevent="goToSection(section.id)">{{ section.title }}</a>
            </nav>
            <a v-if="tenantConfig.videosUrl" class="video-link" :href="tenantConfig.videosUrl" target="_blank" rel="noopener noreferrer">Assistir aos vídeos de treinamento</a>
          </div>
        </aside>

        <section class="manual-content">
          <div class="filter-row">
            <div>
              <h2 v-if="searchTerm">Resultados para “{{ searchTerm }}”</h2>
              <h2 v-else>Manual operacional</h2>
            </div>
            <label class="role-filter">
              <span>Perfil:</span>
              <select v-model="selectedRole" aria-label="Perfil para filtrar orientações">
                <option value="all">Todos os perfis</option>
                <option v-for="role in documentation.roles" :key="role.id" :value="role.id">{{ role.label }}</option>
              </select>
            </label>
          </div>
          <p class="filter-note" v-if="selectedRole !== 'all'">Você está vendo temas úteis para {{ selectedRoleLabel }}, além dos conteúdos compartilhados. Este filtro não altera suas permissões no portal.</p>
          <details class="mobile-summary">
            <summary>Sumário</summary>
            <nav class="toc">
              <a v-for="section in visibleSections" :key="'mobile-' + section.id" :href="'#' + section.id" @click="goToSection(section.id)">{{ section.title }}</a>
            </nav>
          </details>

          <div v-if="visibleSections.length" class="section-list">
            <section v-for="section in visibleSections" :id="section.id" :key="section.id" class="doc-section">
              <div class="section-heading">
                <div>
                  <h2>{{ section.title }}</h2>
                  <p>{{ section.summary }}</p>
                </div>
              </div>
              <article v-for="article in section.articles" :id="article.id" :key="article.id" class="article">
                <h3>{{ article.title }}</h3>
                <p class="article-summary">{{ article.summary }}</p>
                <div class="article-body">
                  <div class="prose">
                    <p v-for="(paragraph, index) in article.paragraphs" :key="'p' + index">{{ paragraph }}</p>
                  </div>
                  <ol v-if="article.steps && article.steps.length" class="steps">
                    <li v-for="(step, index) in article.steps" :key="'s' + index">{{ step }}</li>
                  </ol>
                  <ul v-if="article.tips && article.tips.length" class="article-notes"><li v-for="(tip, index) in article.tips" :key="'t' + index">{{ tip }}</li></ul>
                  <ul v-if="article.warnings && article.warnings.length" class="article-notes"><li v-for="(warning, index) in article.warnings" :key="'w' + index">{{ warning }}</li></ul>
                </div>
              </article>
            </section>
          </div>

          <section v-else class="no-results">
            <h2>Nada encontrado por aqui.</h2>
            <p>Experimente uma palavra diferente ou reveja o filtro de perfil.</p>
            <button class="text-button" type="button" @click="clearFilters">Limpar busca e filtros</button>
          </section>

          <section v-if="documentation.glossary && documentation.glossary.length" id="glossario" class="glossary">
            <h2>Glossário SalesFlow</h2>
            <p>Termos que aparecem durante a jornada do pedido.</p>
            <dl>
              <div v-for="item in documentation.glossary" :key="item.term"><dt>{{ item.term }}</dt><dd>{{ item.definition }}</dd></div>
            </dl>
          </section>
        </section>
      </div>
      <footer class="manual-footer"><div class="manual-shell"><span>{{ tenantConfig.companyName }} · SalesFlow</span><button type="button" @click="scrollTop">Voltar ao início</button></div></footer>
    </template>
  </main>
</template>

<script>
import { getTenantHelpConfig } from '@/documentation/tenantHelp'
import macmillanDocumentation from '@/documentation/macmillan'

export default {
  name: 'DocumentationIndex',
  data () {
    return {
      searchTerm: '',
      selectedRole: 'all',
      activeSection: '',
    }
  },
  computed: {
    tenantSlug () { return this.$route.params.tenantSlug },
    isDark () { return Boolean(this.$store && this.$store.state.appConfig && this.$store.state.appConfig.layout && this.$store.state.appConfig.layout.skin === 'dark') },
    tenantConfig () {
      const config = getTenantHelpConfig(this.tenantSlug)
      return config && this.normalize(config.slug).trim() === this.normalize(this.tenantSlug).trim() ? config : null
    },
    hasDocumentation () { return Boolean(this.tenantConfig && macmillanDocumentation) },
    documentation () { return this.hasDocumentation ? macmillanDocumentation : { roles: [], sections: [], glossary: [] } },
    normalizedQuery () { return this.normalize(this.searchTerm) },
    selectedRoleLabel () {
      const role = this.documentation.roles.find(item => item.id === this.selectedRole)
      return role ? role.label : ''
    },
    visibleSections () {
      return this.documentation.sections.map(section => ({
        ...section,
        articles: section.articles.filter(article => this.matchesArticle(article))
      })).filter(section => section.articles.length)
    }
  },
  watch: {
    '$route.params.tenantSlug': 'resetForTenant',
    '$route.hash': 'openHashArticle'
  },
  mounted () {
    this.openHashArticle()
    window.addEventListener('keydown', this.handleShortcut)
  },
  beforeDestroy () { window.removeEventListener('keydown', this.handleShortcut) },
  methods: {
    normalize (value) { return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() },
    articleText (article) {
      return [article.title, article.summary, ...(article.keywords || []), ...(article.paragraphs || []), ...(article.steps || []), ...(article.tips || []), ...(article.warnings || [])].join(' ')
    },
    matchesArticle (article) {
      const roleMatch = this.selectedRole === 'all' || !article.roles || !article.roles.length || article.roles.includes(this.selectedRole)
      return roleMatch && (!this.normalizedQuery || this.normalize(this.articleText(article)).includes(this.normalizedQuery))
    },
    getArticleById (id) {
      for (const section of this.documentation.sections) {
        const article = section.articles.find(item => item.id === id)
        if (article) return { article, section }
      }
      return null
    },
    openHashArticle () {
      const hash = (this.$route.hash || window.location.hash).replace('#', '')
      if (!hash || !this.hasDocumentation) return
      const found = this.getArticleById(hash)
      if (found && this.matchesArticle(found.article)) {
        this.activeSection = found.section.id
        this.scrollToId(hash)
      } else if (document.getElementById(hash)) {
        this.scrollToId(hash)
      }
    },
    setHash (id) {
      if (this.$route.hash === `#${id}`) return
      this.$router.replace({ name: this.$route.name, params: this.$route.params, query: this.$route.query, hash: `#${id}` }).catch(() => {})
    },
    goToSection (id) {
      this.activeSection = id
      this.setHash(id)
      this.scrollToId(id)
    },
    clearSearch () { this.searchTerm = '' },
    clearFilters () { this.searchTerm = ''; this.selectedRole = 'all' },
    handleShortcut (event) {
      const activeElement = document.activeElement
      if (event.key === '/' && activeElement && !activeElement.isContentEditable && !['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName)) {
        event.preventDefault()
        const input = this.$el.querySelector('.search-box input')
        if (input) input.focus()
      }
    },
    scrollToId (id) {
      this.$nextTick(() => window.requestAnimationFrame(() => {
        const target = document.getElementById(id)
        if (target) target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' })
      }))
    },
    scrollTop () { window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }) },
    resetForTenant () { this.searchTerm = ''; this.selectedRole = 'all'; this.activeSection = ''; this.$nextTick(this.openHashArticle) }
  }
}
</script>

<style scoped lang="scss">
@import './Documentation.scss';
</style>